import type { SubmitHandler } from 'react-hook-form';
import { useState } from 'react';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUpdatePollResponseBody } from '~/lib/api/modules/Polls';
import type { IUseUpdatePollMutationValues } from '~/lib/api/modules/Polls/hooks/useUpdatePollMutation';
import type { IPollFormValues } from './PollForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { usePollQuery } from '~/lib/api/modules/Polls/hooks/usePollQuery';
import { useUpdatePollMutation } from '~/lib/api/modules/Polls/hooks/useUpdatePollMutation';
import { PollForm } from './PollForm';

export type IUpdatePollFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiUpdatePollResponseBody,
    inputValues: IUseUpdatePollMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
  pollId: string;
};

export const UpdatePollForm: React.FC<IUpdatePollFormProps> = (props) => {
  const { sx, onSuccess, cancelUrl, appId, pollId } = props;
  const pollQuery = usePollQuery({ appId, pollId });
  const updatePollMutation = useUpdatePollMutation();
  const [disabled, setDisabled] = useState(false);

  const poll = pollQuery.data;

  const handleSubmit: SubmitHandler<IPollFormValues> = (values) => {
    if (updatePollMutation.isLoading) {
      return;
    }

    return updatePollMutation.mutateAsync(
      {
        appId,
        pollId,
        body: {
          active: !values.inactive,
          canUpdate: values.canUpdate,
          description: values.description,
          displayResults: values.displayResults,
          endDate: values.endDate!,
          multipleChoices: values.multipleChoices,
          options: values.options!.split('\n').map((option, optionIndex) => ({
            id: poll?.options?.[optionIndex].id ?? self.crypto.randomUUID(),
            text: option.trim(),
          })),
          requires: values.requires,
          startDate: values.startDate!,
          title: values.title!,
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock draft={disabled} sx={sx} draftOutlineSize="lg">
      <ContentBlock.Suspense
        loading={pollQuery.isLoading}
        error={pollQuery.error}
        first
      >
        {poll && (
          <PollForm
            mode="edition"
            cancelUrl={cancelUrl}
            defaultValues={{
              title: poll.title,
              description: poll.description,
              options: poll.options?.map((option) => option.text).join('\n'),
              startDate: poll.startDate,
              endDate: poll.endDate,
              requires: poll.requires,
              displayResults: poll.displayResults,
              multipleChoices: poll.multipleChoices,
              canUpdate: poll.canUpdate,
              inactive: !poll.active,
            }}
            onSubmit={handleSubmit}
            onDisabledStatusChange={setDisabled}
            first
          />
        )}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
